import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pizza, KeyRound, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import PasswordStrengthMeter from "../components/PasswordStrengthMeter";
import { evaluatePassword } from "../lib/passwordPolicy";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const { valid } = evaluatePassword(pw);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!valid) { setError(t("security.policyHint")); return; }
    if (pw !== confirm) { setError(t("changePassword.mismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password-confirm", { token, new_password: pw });
      setDone(true);
      toast.success(t("resetPassword.success"));
    } catch (err) {
      const msg = err?.response?.data?.detail || t("common.error");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50 relative">
      <div className="absolute top-4 right-4"><LanguageSwitcher variant="login" /></div>
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
        <div className="flex items-center gap-2.5 mb-5">
          <div className="w-10 h-10 rounded-xl bg-[#FFDF00] flex items-center justify-center">
            <Pizza className="w-6 h-6 text-[#1A1A1A]" strokeWidth={2.5} />
          </div>
          <div className="font-display font-extrabold text-xl">Chizy Chiz</div>
        </div>

        {!token ? (
          <div data-testid="reset-invalid" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            {t("resetPassword.invalidLink")}
          </div>
        ) : done ? (
          <div data-testid="reset-success" className="text-center py-6">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
            <h2 className="font-display font-extrabold text-xl text-[#1A1A1A]">{t("resetPassword.success")}</h2>
            <button
              data-testid="reset-go-login"
              onClick={() => navigate("/login")}
              className="mt-5 btn-primary-chizy py-3 px-6 rounded-xl inline-flex items-center gap-2"
            >
              {t("twofa.backToLogin")} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <h2 className="font-display font-extrabold text-2xl text-[#1A1A1A] tracking-tight flex items-center gap-2">
              <KeyRound className="w-6 h-6 text-amber-500" />
              {t("resetPassword.title")}
            </h2>
            <p className="text-slate-500 mt-1.5 text-sm">{t("resetPassword.subtitle")}</p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-bold text-[#1A1A1A] uppercase tracking-wider">{t("changePassword.newPassword")}</label>
                <input
                  data-testid="reset-new-password"
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="mt-1.5 w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]"
                  required
                  autoFocus
                />
                <PasswordStrengthMeter password={pw} />
              </div>
              <div>
                <label className="text-xs font-bold text-[#1A1A1A] uppercase tracking-wider">{t("changePassword.confirm")}</label>
                <input
                  data-testid="reset-confirm-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1.5 w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]"
                  required
                />
              </div>

              {error && (
                <div data-testid="reset-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
                  {error}
                </div>
              )}

              <button
                data-testid="reset-submit"
                type="submit"
                disabled={loading || !valid || !confirm}
                className="w-full btn-primary-chizy py-3.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? t("common.saving") : (<>{t("resetPassword.submit")} <ArrowRight className="w-4 h-4" strokeWidth={2.5} /></>)}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
